import {
  MRT_ColumnDef,
  MRT_RowData,
  MRT_SortingState,
} from 'material-react-table';

/** Resultado pronto para uso em Material React Table (columns + data). */
export type MRTTableConfig<T extends MRT_RowData = Record<string, string>> = {
  columns: MRT_ColumnDef<T>[];
  data: T[];
};

export type SeparatorType = ';' | '|' | '\t';

export type ProcessRunFidcResponseType = {
  data: string;
  processedAmount: number;
  notFoundAmount: number;
  foundAmount: number;
};

const SEPARATOR_CANDIDATES: SeparatorType[] = ['\t', ';', '|'];

export class ImportFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImportFormatError';
  }
}

/**
 * Remove apenas \\r do fim da linha (normalização), sem remover tab/espaços,
 * para preservar colunas vazias ao fazer split pelo separador.
 */
function normalizeLineEnd(line: string): string {
  return line.replace(/\r$/, '');
}

/**
 * Identifica qual SeparatorType foi usado nas linhas.
 * Testa tab, pipe e ponto e vírgula; retorna o que mantiver número de colunas
 * consistente no cabeçalho e nas primeiras linhas (até 5 linhas).
 * Não aplica trim na linha antes do split, para preservar colunas vazias (ex.: tab no fim).
 */
export function detectSeparator(lines: string[]): SeparatorType | null {
  if (!lines?.length) return null;

  const linesToCheck = lines
    .map(normalizeLineEnd)
    .filter((line) => line.trim().length > 0)
    .slice(0, 5);

  if (linesToCheck.length < 2) return null;

  for (const sep of SEPARATOR_CANDIDATES) {
    const columnCounts = linesToCheck.map((line) => line.split(sep).length);
    const expectedCount = Math.max(...columnCounts);

    if (expectedCount <= 1) continue;

    const allowed = columnCounts.every(
      (count) => count >= expectedCount - 1 && count <= expectedCount,
    );
    if (allowed) return sep;
  }

  return null;
}

/**
 * Normaliza o texto colado em linhas e detecta o separador (tab, pipe ou ponto e vírgula).
 * Lança ImportFormatError se não houver linhas ou se o separador não for identificado.
 */
export function parseTableDataToLinesAndSeparator(tableData: string): {
  lines: string[];
  separator: SeparatorType;
} {
  const lines = tableData
    .split(/\r?\n/)
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim().length > 0);

  if (!lines.length) {
    throw new ImportFormatError('Cole os dados da tabela no campo acima.');
  }

  const separator = detectSeparator(lines);
  if (!separator) {
    throw new ImportFormatError(
      'Utilize apenas um único separador em todo o documento. Os separadores suportados são: tab, pipe ou ponto e vírgula.',
    );
  }

  return { lines, separator };
}

/**
 * Parse linhas de um arquivo estruturado (colunas separadas por tab, pipe ou ponto e vírgula).
 * A primeira linha é o cabeçalho (nomes das colunas) e não entra como dado.
 * Não aplica trim na linha antes do split: faz split primeiro e depois trim em cada célula,
 * para preservar colunas vazias (ex.: célula vazia no fim da linha ou entre separadores).
 * Linhas com menos colunas que o cabeçalho são preenchidas com ''; com mais, são truncadas.
 */
export function parseDataBySeparatorType(
  lines: string[],
  separator: SeparatorType,
): { headers: string[]; rows: Record<string, string>[] } {
  if (!lines?.length) {
    throw new ImportFormatError('Nenhuma linha fornecida.');
  }

  if (lines.length < 2) {
    throw new ImportFormatError(
      'É necessário pelo menos a linha de cabeçalho e uma linha de dados.',
    );
  }

  const headerRaw = normalizeLineEnd(lines[0]);
  const headers = headerRaw.split(separator).map((h) => h.trim());

  if (headers.length === 0 || headers.some((h) => !h)) {
    throw new ImportFormatError(
      'A linha de cabeçalho está vazia ou inválida para o separador informado.',
    );
  }

  const expectedCols = headers.length;
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const rawLine = normalizeLineEnd(lines[i]);
    if (rawLine.trim().length === 0) continue;

    const values = rawLine.split(separator).map((v) => v.trim());

    const padded: string[] =
      values.length >= expectedCols
        ? values.slice(0, expectedCols)
        : [...values, ...Array(expectedCols - values.length).fill('')];

    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = padded[idx] ?? '';
    });
    rows.push(row);
  }

  return { headers, rows };
}

/**
 * A partir do resultado de parseDataBySeparatorType, monta columns e data
 * no formato esperado pelo Material React Table para uso posterior.
 */
export function setColumnsByData(parsed: {
  headers: string[];
  rows: Record<string, string>[];
}): MRTTableConfig {
  const columns: MRT_ColumnDef<Record<string, string>>[] = parsed.headers.map(
    (header) => ({
      header,
      accessorKey: header,
    }),
  );

  return {
    columns,
    data: parsed.rows,
  };
}

/**
 * Processa os dados brutos de uma tabela (string) para extrair as linhas e identificar o separador.
 * Lança ImportFormatError em caso de dados vazios ou separador não identificado.
 */
export function processTableData(tableData: string): {
  lines: string[];
  separator: SeparatorType;
} {
  const lines = tableData
    .split(/\r?\n/)
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim().length > 0);

  if (!lines.length) {
    throw new ImportFormatError('Cole os dados da tabela no campo acima.');
  }

  const separator = detectSeparator(lines);
  if (!separator) {
    throw new ImportFormatError(
      'Não foi possível identificar o separador. Use tab, pipe ou ponto e vírgula.',
    );
  }
  return { lines, separator };
}

/**
 * Gera o texto exportável (TSV) da tabela a partir da config e ordenação.
 */
export function exportTableDataAsText(config: {
  columns: MRT_ColumnDef<Record<string, string>>[];
  data: Record<string, string>[];
  sorting?: MRT_SortingState;
}): string {
  const { columns, data, sorting = [] } = config;
  if (!columns.length || !data.length) return '';

  const separator = '\t';
  const headerLine = columns.map((c) => c.header).join(separator);

  const sortedData =
    sorting.length > 0
      ? [...data].sort((a, b) => {
          for (const s of sorting) {
            const key = s.id;
            const va = a[key] ?? '';
            const vb = b[key] ?? '';
            const cmp = String(va).localeCompare(String(vb), undefined, {
              numeric: true,
            });
            if (cmp !== 0) return s.desc ? -cmp : cmp;
          }
          return 0;
        })
      : data;

  const dataLines = sortedData.map((row) =>
    columns
      .map((c) => {
        const value = row[c.accessorKey ?? ''];
        return value != null ? String(value) : '';
      })
      .join(separator),
  );

  return [headerLine, ...dataLines].join('\n');
}
